import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Breadcrumbs from "../based/Breadcrumbs";
import CommonTable from "../based/CommonTable";
import { INITPAGING, NOTIFY, WalletWithdrawStatus } from "../based/Constants";
import { ConfirmDialog } from "../based/Dialog";
import CheckBox from "../based/inputs/CheckBox";
import { Notify } from "../based/Notify";
import FinanceServices from "../based/services/FinanceServices";
import WithdrawalCancelModal from "./WithdrawalCancelModal";
import WithdrawalDetailModal from "./WithdrawalDetailModal";

const STATUS_TABS = [
    { value: WalletWithdrawStatus.ALL, label: "Tất cả" },
    { value: WalletWithdrawStatus.PENDING, label: "Khách yêu cầu" },
    { value: WalletWithdrawStatus.APPROVED, label: "Đợi tạo lệnh" },
    { value: WalletWithdrawStatus.PROCESSING, label: "Đang xử lý" },
    { value: WalletWithdrawStatus.COMPLETED, label: "Hoàn thành" },
    { value: WalletWithdrawStatus.CANCELED, label: "Đã hủy" },
    { value: WalletWithdrawStatus.REJECTED, label: "Từ chối" },
];

const STATUS_META = {
    [WalletWithdrawStatus.PENDING]: { label: "Khách yêu cầu", bg: "#ff9027", color: "#fff" },
    [WalletWithdrawStatus.APPROVED]: { label: "Đợi tạo lệnh", bg: "#2cb0fb", color: "#fff" },
    [WalletWithdrawStatus.PROCESSING]: { label: "Đang xử lý", bg: "#64c5b1", color: "#fff" },
    [WalletWithdrawStatus.COMPLETED]: { label: "Hoàn thành", bg: "#28b259", color: "#fff" },
    [WalletWithdrawStatus.CANCELED]: { label: "Đã hủy", bg: "#888", color: "#fff" },
    [WalletWithdrawStatus.REJECTED]: { label: "Từ chối", bg: "#e74c3c", color: "#fff" },
};

function formatMoney(amount) {
    if (!amount && amount !== 0) return "—";
    return new Intl.NumberFormat("vi-VN").format(amount) + "đ";
}

function formatDateTime(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")} ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
}

function getDaysWaiting(dateStr) {
    if (!dateStr) return 0;
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

export default function Withdrawal() {
    const [isBusy, setBusy] = useState(false);
    const [paging, setPaging] = useState({ ...INITPAGING, status: WalletWithdrawStatus.ALL });
    const [datas, setDatas] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [selectedIds, setSelectedIds] = useState([]);
    const [detailId, setDetailId] = useState(null);
    const [cancelId, setCancelId] = useState(null);
    const keywordRef = useRef("");

    useEffect(() => {
        getData(paging);
    }, []);

    async function getData(page) {
        setBusy(true);
        setSelectedIds([]);
        const [err, data] = await FinanceServices.GetAdminWithdrawalRequests(page);
        if (!err && data) {
            setDatas(data.listObjects || []);
            setPaging(data.paging);
            setStatusCounts(data.statusCounts || {});
        } else {
            Notify(NOTIFY.ERROR, "Lỗi", "Không thể tải danh sách yêu cầu rút tiền");
        }
        setBusy(false);
    }

    function handleSearch() {
        getData({ ...paging, pageNumber: 1, keyword: keywordRef.current });
    }

    function handleSelectAll(checked) {
        if (checked) {
            const approvedIds = datas.filter((d) => d.status === WalletWithdrawStatus.APPROVED).map((d) => d.id);
            setSelectedIds((prev) => [...new Set([...prev, ...approvedIds])]);
        } else {
            setSelectedIds([]);
        }
    }

    function handleSelectOne(id, checked) {
        setSelectedIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
    }

    async function handleBatchCreateOrder() {
        ConfirmDialog("Xác nhận", `Xác nhận tạo lệnh chuyển tiền cho ${selectedIds.length} yêu cầu?`, async () => {
            const [err] = await FinanceServices.CreateBatchSyncTransfer(selectedIds);
            if (err) throw { title: "Lỗi", msg: "Tạo lệnh thất bại" };
            setSelectedIds([]);
            getData(paging);
            return { title: "Thành công", msg: "Đã tạo lệnh chuyển tiền" };
        });
    }

    const approvedOnPage = datas.filter((d) => d.status === WalletWithdrawStatus.APPROVED).map((d) => d.id);
    const allSelected = approvedOnPage.length > 0 && approvedOnPage.every((id) => selectedIds.includes(id));
    const approvedSelectedIds = selectedIds.filter((id) => datas.find((d) => d.id === id)?.status === WalletWithdrawStatus.APPROVED);
    const canBatchOrder = approvedSelectedIds.length > 0;

    function _renderAction() {
        return (
            <div className="action-bar">
                {canBatchOrder && (
                    <button className="btn-batch-order" onClick={handleBatchCreateOrder}>
                        Tạo lệnh ({approvedSelectedIds.length})
                    </button>
                )}

                <div className="search-box">
                    <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Tìm kiếm mã yêu cầu/số điện thoại"
                        defaultValue={paging.keyword}
                        onChange={(e) => (keywordRef.current = e.target.value)}
                        onKeyPress={(e) => e.charCode === 13 && handleSearch()}
                    />
                    <button className="btn-search" onClick={handleSearch}>
                        Tìm kiếm
                    </button>
                </div>
                <div className="date-range">
                    <input type="date" className="date-input" value={paging.fromDate} onChange={(e) => setPaging((p) => ({ ...p, fromDate: e.target.value }))} />
                    <span className="date-sep">—</span>
                    <input type="date" className="date-input" value={paging.toDate} onChange={(e) => setPaging((p) => ({ ...p, toDate: e.target.value }))} />
                    <span className="date-icon">&#128197;</span>
                </div>
            </div>
        );
    }

    function _renderHeader() {
        return (
            <thead>
                <tr>
                    <th className="text-center col-check">
                        <CheckBox checked={allSelected} onCheckedChange={(name, val) => handleSelectAll(val)} name="select-all" />
                    </th>
                    <th className="text-center col-account">Tài khoản</th>
                    <th className="text-center col-bank">Thông tin hoàn tiền</th>
                    <th className="text-center col-time">Thời gian yêu cầu</th>
                    <th className="text-center col-amount">Số tiền rút</th>
                    <th className="text-center col-status">Trạng thái</th>
                    <th className="text-center col-note">Note</th>
                    <th className="text-center col-action">Thao tác</th>
                </tr>
            </thead>
        );
    }

    function _renderBody() {
        if (!datas || datas.length === 0)
            return (
                <tbody>
                    <tr>
                        <td colSpan={8} className="text-center vertical-align-middle">
                            Không có dữ liệu
                        </td>
                    </tr>
                </tbody>
            );
        return (
            <tbody>
                {datas.map((item) => (
                    <WithdrawalRow
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.includes(item.id)}
                        onSelect={(checked) => handleSelectOne(item.id, checked)}
                        onDetail={() => setDetailId(item.id)}
                        onCancel={() => setCancelId(item.id)}
                        onRefresh={() => getData(paging)}
                    />
                ))}
            </tbody>
        );
    }

    return (
        <Wrapper className="container-fluid">
            <Breadcrumbs currentPage="Quản lý rút tiền ví Ranus" />
            <div className="mb-2">
                <p className="flow-desc">Khách yêu cầu &gt; Duyệt và chuyển Đợi tạo lệnh &gt; Tạo lệnh &gt; Đang duyệt chi &gt; Ngân hàng xử lý &gt; Thành công/Hủy (Thất bại)</p>
            </div>
            <ul className="nav nav-tabs tabs-bordered">
                {STATUS_TABS.map((tab) => (
                    <li key={tab.value} className="nav-item" onClick={() => getData({ ...paging, status: tab.value, pageNumber: 1 })}>
                        <a className={`nav-link d-flex align-items-center ${paging.status === tab.value ? "active" : ""}`}>
                            {tab.label}
                            {statusCounts[tab.value] > 0 && <span className="count-badge">{statusCounts[tab.value]}</span>}
                        </a>
                    </li>
                ))}
            </ul>
            <CommonTable datas={datas} paging={paging} renderAction={_renderAction} renderHeader={_renderHeader} renderBody={_renderBody} onFilter={getData} />
            <WithdrawalDetailModal show={!!detailId} requestId={detailId} onClose={() => setDetailId(null)} onStatusChanged={() => getData(paging)} />
            <WithdrawalCancelModal
                show={!!cancelId}
                requestId={cancelId}
                onClose={() => setCancelId(null)}
                onSuccess={() => {
                    setCancelId(null);
                    getData(paging);
                }}
            />
        </Wrapper>
    );
}

function WithdrawalRow({ item, isSelected, onSelect, onDetail, onCancel }) {
    const statusMeta = STATUS_META[item.status] || { label: "?", bg: "#888", color: "#fff" };
    const isAbnormal = item.isNewAccount || item.isDifferentAccount;
    const daysWaiting = getDaysWaiting(item.requestTime);

    async function handleCreateOrder() {
        ConfirmDialog("Tạo lệnh", `Xác nhận tạo lệnh chuyển tiền cho yêu cầu ${item.requestCode}?`, async () => {
            return { title: "Thành công", msg: "Đã tạo lệnh" };
        });
    }

    function _renderStatusSecondary() {
        if (item.status === WalletWithdrawStatus.PENDING || item.status === WalletWithdrawStatus.APPROVED) {
            return <div className="status-sub">Đã chờ {daysWaiting} ngày</div>;
        }
        if (item.status === WalletWithdrawStatus.PROCESSING && item.processedDate) {
            return <div className="status-sub">Đã tạo lệnh {formatDateTime(item.processedDate)}</div>;
        }
        if (item.status === WalletWithdrawStatus.CANCELED && item.completedDate) {
            return <div className="status-sub status-sub--success">{formatDateTime(item.completedDate)}</div>;
        }
        if (item.status === WalletWithdrawStatus.REJECTED) {
            return (
                <div className="status-sub status-sub--rejected">
                    {item.canceledAt && <div>Admin hủy {formatDateTime(item.canceledAt)}</div>}
                    {item.cancelReasonText && <div>{item.cancelReasonText}</div>}
                </div>
            );
        }
        return null;
    }

    return (
        <tr style={{ background: isSelected ? "#cef4ec" : isAbnormal ? "#ffe6e6" : "", borderLeft: isAbnormal ? "4px solid #d70000" : "" }}>
            <td>
                <CheckBox
                    checked={isSelected}
                    onCheckedChange={(name, value) => {
                        if (item.status !== WalletWithdrawStatus.APPROVED) {
                            Notify(NOTIFY.WARNING, "Không thể chọn", "Chỉ có thể chọn yêu cầu ở trạng thái Đợi tạo lệnh để thực hiện thao tác hàng loạt");
                            return;
                        }
                        onSelect(value);
                    }}
                    name={`sel-${item.id}`}
                />
            </td>
            <td className="cell-account">
                <div>
                    <strong>Mã yêu cầu:</strong> {item.requestCode}
                </div>
                <div>{item.customerName}</div>
                <div className="phone-row">
                    <span>{item.phone}</span>
                    {item.phone && (
                        <a className="zalo-btn" href={`https://zalo.me/${item.phone}`} target="_blank" rel="noreferrer">
                            Nhắn Zalo
                        </a>
                    )}
                </div>
            </td>
            <td className="cell-bank">
                <div>{item.bankName}</div>
                <div>{item.accountNumber}</div>
                <div>{item.accountName}</div>
            </td>
            <td className="text-nowrap cell-time">{formatDateTime(item.requestTime)}</td>
            <td className="cell-amount">{formatMoney(item.amount)}</td>
            <td className="text-center">
                <div className="status-pill" style={{ background: statusMeta.bg, color: statusMeta.color }}>
                    {statusMeta.label}
                </div>
                {_renderStatusSecondary()}
            </td>
            <td>
                <div className="tags-wrap">
                    {item.withdrawCount != null && <span className="tag tag-grey">{item.withdrawCount} lần rút</span>}
                    {item.isNewAccount && (
                        <span className="tag tag-red">
                            <i className="fa fa-play fa-icon-sm" />
                            Tài khoản mới
                        </span>
                    )}
                    {item.isDifferentAccount && (
                        <span className="tag tag-red">
                            <i className="fa fa-play fa-icon-sm" />
                            STK khác lần cuối giao dịch
                        </span>
                    )}
                </div>
            </td>
            <td>
                <div className="action-btns-col">
                    {item.status === 2 && (
                        <button className="row-btn btn-teal" onClick={handleCreateOrder}>
                            Tạo lệnh
                        </button>
                    )}
                    <button className="row-btn btn-teal-outline" onClick={onDetail}>
                        Chi tiết
                    </button>
                    {(item.status === 1 || item.status === 2) && (
                        <button className="row-btn btn-red-outline" onClick={onCancel}>
                            Hủy yêu cầu
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}

const Wrapper = styled.div`
    /* Tabs */
    .nav-tabs {
        border-bottom: none;
    }
    .nav-tabs .nav-item .nav-link {
        background: #f8f9fa;
        border: none;
        border-bottom: 3px solid #dfe2e4;
        border-radius: 0;
        color: #666f7b;
        font-weight: 600;
        font-size: 16px;
        padding: 11px 16px;
        cursor: pointer;
        white-space: nowrap;
    }
    .nav-tabs .nav-item .nav-link.active {
        background: #fff;
        border-bottom: 3px solid #64c5b1;
        color: #666f7b;
    }
    .count-badge {
        background: #d70000;
        color: #fff;
        border-radius: 20px;
        min-width: 20px;
        height: 20px;
        font-size: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        margin-left: 4px;
    }

    /* Flow description */
    .flow-desc {
        color: #666f7b;
        font-size: 14px;
        margin-bottom: 0;
    }

    /* Action bar */
    .action-bar {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        margin-bottom: 12px;
    }
    .btn-batch-order {
        background: #64c5b1;
        color: #fff;
        font-weight: 700;
        font-size: 16px;
        height: 38px;
        padding: 0 13px;
        border: none;
        border-radius: 2px;
        white-space: nowrap;
        cursor: pointer;
    }
    .btn-batch-waiting {
        background: #64c5b1;
        color: #fff;
        font-weight: 600;
        font-size: 14px;
        height: 38px;
        padding: 0 13px;
        border: none;
        border-radius: 2px;
        white-space: nowrap;
        cursor: pointer;
    }
    .search-box {
        display: flex;
        height: 36px;
    }
    .search-input {
        width: 256px !important;
        height: 36px !important;
        border: 1px solid #d9d9d9 !important;
        border-radius: 0 !important;
        font-size: 14px !important;
    }
    .btn-search {
        background: #343c49;
        color: #fff;
        font-weight: 600;
        font-size: 14px;
        height: 36px;
        padding: 0 20px;
        border: none;
        white-space: nowrap;
        cursor: pointer;
    }
    .date-range {
        display: flex;
        align-items: center;
        height: 36px;
        border: 1px solid #d9d9d9;
        background: #fff;
        padding: 0 10px;
        gap: 6px;
    }
    .date-input {
        border: none;
        outline: none;
        font-size: 14px;
        color: #444;
        width: 120px;
        background: transparent;
    }
    .date-sep {
        color: #444;
        font-size: 14px;
    }
    .date-icon {
        color: #888;
        font-size: 18px;
        line-height: 1;
    }

    /* Table column widths */
    .col-check {
        width: 40px;
    }
    .col-account {
        min-width: 180px;
    }
    .col-bank {
        min-width: 200px;
    }
    .col-time {
        min-width: 140px;
    }
    .col-amount {
        min-width: 110px;
        text-align: right;
    }
    .col-status {
        min-width: 180px;
    }
    .col-note {
        min-width: 220px;
    }
    .col-action {
        min-width: 120px;
    }

    /* Cell content */
    .cell-account {
        font-size: 14px;
    }
    .cell-bank {
        font-size: 14px;
    }
    .cell-time {
        font-size: 14px;
    }
    .cell-amount {
        font-weight: 600;
        font-size: 20px;
        white-space: nowrap;
        text-align: center;
    }
    .phone-row {
        display: flex;
        align-items: center;
        gap: 6px;
    }
    .zalo-btn {
        background: #0067f4;
        color: #fff;
        border-radius: 80px;
        padding: 0 8px;
        font-size: 12px;
        white-space: nowrap;
        text-decoration: none;
    }
    .zalo-btn:hover {
        color: #fff !important;
        text-decoration: none;
    }

    /* Status */
    .status-pill {
        border-radius: 30px;
        padding: 4px 10px;
        font-size: 14px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        height: 28px;
        margin-bottom: 4px;
    }
    .status-sub {
        font-size: 13px;
        color: #666;
        margin-top: 4px;
    }
    .status-sub--success {
        color: #28b259;
    }
    .status-sub--rejected {
        color: #f96a74;
    }

    /* Tags */
    .tags-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }
    .tag {
        border-radius: 6px;
        padding: 3px 8px;
        font-size: 12px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }
    .tag-grey {
        background: #f1f1f1;
        border: 1px solid #ededed;
        color: #444;
    }
    .tag-red {
        background: #ffcaca;
        border: 1px solid #ededed;
        color: #d70000;
    }
    .tag-green {
        background: #cdffdf;
        color: #28b259;
    }
    .fa-icon-sm {
        font-size: 10px;
    }

    /* Row action buttons */
    .action-btns-col {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .row-btn {
        border-radius: 2px;
        padding: 6px 26px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        text-align: center;
        display: block;
        height: 28px;
        line-height: 16px;
        border: none;
    }
    .btn-teal {
        background: #64c5b1;
        color: #fff;
    }
    .btn-teal-outline {
        background: #fff;
        color: #64c5b1;
        border: 1px solid #64c5b1 !important;
    }
    .btn-red-outline {
        background: #fff;
        color: #f96a74;
        border: 1px solid #f96a74 !important;
    }
`;
